import { Box, Typography } from "@mui/material";
import { LineChart } from "@mui/x-charts/LineChart";
import { BarChart } from "@mui/x-charts/BarChart";
import { TrendData } from "./types";

export const InventoryChart = ({ trendData }: { trendData: TrendData }) => {
  const hasInStockRateData =
    trendData.in_stock_rate && trendData.in_stock_rate.length > 0;
  const hasInventoryValueData =
    trendData.inventory_value && trendData.inventory_value.length > 0;

  if (!hasInStockRateData && !hasInventoryValueData) {
    return (
      <Box sx={{ p: 2, backgroundColor: "action.hover", borderRadius: 1, mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No inventory trend data available
        </Typography>
      </Box>
    );
  }

  // For now, show separate charts if we don't have both data types
  // MUI X-Charts has limitations with combining BarChart and LineChart on same axes
  if (hasInStockRateData && !hasInventoryValueData) {
    const periods = trendData.in_stock_rate.map((d) => d.period);
    const rates = trendData.in_stock_rate.map((d) => d.value);

    return (
      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          In-Stock Rate Trend
        </Typography>
        <Box
          sx={{
            backgroundColor: "background.paper",
            borderRadius: 1,
            p: 3,
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
          }}
        >
          <LineChart
            xAxis={[
              {
                data: periods,
                scaleType: "band",
                label: "Period",
              },
            ]}
            yAxis={[
              {
                label: "In-Stock Rate (%)",
                min: 0,
                max: 100,
              },
            ]}
            series={[
              {
                data: rates,
                label: "In-Stock Rate (%)",
                color: "#7b1fa2",
                showMark: true,
              },
            ]}
            grid={{ horizontal: true, vertical: false }}
            sx={{
              "& .MuiChartsGrid-line": {
                stroke: "#e0e0e0",
                strokeWidth: 1,
              },
              "& .MuiChartsAxis-line": {
                stroke: "#bdbdbd",
              },
              "& .MuiChartsAxis-tick": {
                stroke: "#bdbdbd",
              },
            }}
            height={300}
            margin={{ left: 70, right: 40, top: 20, bottom: 60 }}
          />
        </Box>
      </Box>
    );
  }

  if (!hasInStockRateData && hasInventoryValueData) {
    const periods = trendData.inventory_value.map((d) => d.period);
    const values = trendData.inventory_value.map((d) => d.value / 1000000); // Convert to millions

    return (
      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          Inventory Value Trend
        </Typography>
        <Box
          sx={{
            backgroundColor: "background.paper",
            borderRadius: 1,
            p: 3,
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
          }}
        >
          <BarChart
            xAxis={[
              {
                data: periods,
                scaleType: "band",
                label: "Period",
              },
            ]}
            yAxis={[
              {
                label: "Inventory Value ($M)",
              },
            ]}
            series={[
              {
                data: values,
                label: "Inventory Value ($M)",
                color: "#ef6c00",
              },
            ]}
            grid={{ horizontal: true, vertical: false }}
            sx={{
              "& .MuiChartsGrid-line": {
                stroke: "#e0e0e0",
                strokeWidth: 1,
              },
              "& .MuiChartsAxis-line": {
                stroke: "#bdbdbd",
              },
              "& .MuiChartsAxis-tick": {
                stroke: "#bdbdbd",
              },
            }}
            height={300}
            margin={{ left: 70, right: 40, top: 20, bottom: 60 }}
          />
        </Box>
      </Box>
    );
  }

  // Both datasets available - show in-stock rate with inventory value
  // Align periods (use inventory value periods as they're typically more granular)
  const periods = trendData.inventory_value.map((d) => d.period);
  const inventoryValues = trendData.inventory_value.map((d) => d.value / 1000000);

  // Match in-stock rate data to inventory periods
  const inStockRateMap = new Map(
    trendData.in_stock_rate.map((d) => [d.period, d.value])
  );
  const inStockRates = periods.map((period) => inStockRateMap.get(period) || null);

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        Inventory Health
      </Typography>
      <Box
        sx={{
          backgroundColor: "background.paper",
          borderRadius: 1,
          p: 3,
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
        }}
      >
        <LineChart
          xAxis={[
            {
              data: periods,
              scaleType: "band",
              label: "Period",
            },
          ]}
          yAxis={[
            {
              id: "rate",
              label: "In-Stock Rate (%)",
              position: "left",
              min: 0,
              max: 100,
            },
            {
              id: "value",
              label: "Inventory Value ($M)",
              position: "right",
            },
          ]}
          series={[
            {
              id: "inStockRate",
              data: inStockRates,
              label: "In-Stock Rate (%)",
              yAxisId: "rate",
              color: "#7b1fa2",
              showMark: true,
            },
            {
              id: "inventoryValue",
              data: inventoryValues,
              label: "Inventory Value ($M)",
              yAxisId: "value",
              color: "#ef6c00",
              showMark: true,
            },
          ]}
          grid={{ horizontal: true, vertical: false }}
          sx={{
            "& .MuiChartsGrid-line": {
              stroke: "#e0e0e0",
              strokeWidth: 1,
            },
            "& .MuiChartsAxis-line": {
              stroke: "#bdbdbd",
            },
            "& .MuiChartsAxis-tick": {
              stroke: "#bdbdbd",
            },
          }}
          height={350}
          margin={{ left: 70, right: 70, top: 20, bottom: 60 }}
        />
      </Box>
    </Box>
  );
};
